import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { ApiShopService } from '../../../../services/shop/api/api-shop.service';
import { ShopService } from '../../../../services/shop/shop.service';
import { AlertsService } from '../../../../services/alerts.service';
import { LanguagesService } from '../../../../services/languages/languages.service';
import { ShopPack } from '../../../../models/shop/shop-pack.model';
import { ShopPackTranslation } from '../../../../models/shop/shop-pack-translation.model';
import { ShopPackReward } from '../../../../models/shop/shop-pack-reward.model';
import { ShopPackBooster } from '../../../../models/shop/shop-pack-booster.model';
import { UserCurrencyType } from '../../../../enums/users/user-currency-type.enum';
import { UserBoosterType } from '../../../../enums/users/user-booster-type.enum';

interface TranslationForm {
  languageId: number;
  languageIso: string;
  title: string;
  description: string;
  isCollapsed: boolean;
}

@Component({
  selector: 'app-shop-pack-edit-modal',
  templateUrl: './shop-pack-edit-modal.component.html',
  standalone: true,
  imports: [ModalModule, CommonModule, FormsModule]
})
export class ShopPackEditModalComponent {

  @ViewChild('modal') modal!: ModalDirective;

  isLoading: boolean = false;
  packId: number = 0;
  name: string = '';
  startDate: string = '';
  endDate: string = '';
  shopItemId: number = 0;
  translations: TranslationForm[] = [];
  rewards: ShopPackReward[] = [];
  boosters: ShopPackBooster[] = [];

  readonly currencyTypes = Object.entries(UserCurrencyType).filter(([, v]) => typeof v === 'number') as [string, number][];
  readonly boosterTypes = Object.entries(UserBoosterType).filter(([, v]) => typeof v === 'number') as [string, number][];

  private callbackSaved?: () => void;

  constructor(
    private apiShopService: ApiShopService,
    public shopService: ShopService,
    public languagesService: LanguagesService,
    private alertsService: AlertsService
  ) {}

  show(pack: ShopPack, callbackSaved?: () => void): void {
    this.callbackSaved = callbackSaved;
    this.packId = pack.packId;
    this.name = pack.name;
    this.startDate = pack.dateStartTimestamp ? this.timestampToDateString(pack.dateStartTimestamp) : '';
    this.endDate = pack.dateEndTimestamp ? this.timestampToDateString(pack.dateEndTimestamp) : '';
    this.shopItemId = pack.shopItemId;
    this.isLoading = false;
    this.translations = this.languagesService.languages.map(l => {
      const existing = pack.translations.find(t => t.languageId === l.languageId);
      return { languageId: l.languageId, languageIso: l.languageIso, title: existing?.title ?? '', description: existing?.description ?? '', isCollapsed: true };
    });
    this.rewards = pack.rewards.map(r => { const rw = new ShopPackReward(); rw.shopItemId = r.shopItemId; rw.currencyType = r.currencyType; rw.currencyQuantity = r.currencyQuantity; rw.currencyTicketCost = r.currencyTicketCost; rw.currencyIncreasePercent = r.currencyIncreasePercent; return rw; });
    this.boosters = pack.boosters.map(b => { const bst = new ShopPackBooster(); bst.boosterType = b.boosterType; bst.boosterMultiplier = b.boosterMultiplier; bst.boosterDuration = b.boosterDuration; return bst; });
    this.modal.show();
  }

  toggleTranslation(t: TranslationForm): void { t.isCollapsed = !t.isCollapsed; }
  addReward(): void { this.rewards.push(new ShopPackReward()); }
  removeReward(i: number): void { this.rewards.splice(i, 1); }
  addBooster(): void { this.boosters.push(new ShopPackBooster()); }
  removeBooster(i: number): void { this.boosters.splice(i, 1); }

  save(): void {
    this.isLoading = true;
    const startTimestamp = this.startDate ? Math.floor(new Date(this.startDate + 'Z').getTime() / 1000) : 0;
    const endTimestamp = this.endDate ? Math.floor(new Date(this.endDate + 'Z').getTime() / 1000) : 0;
    const translationsPayload = this.translations.map(t => { const tr = new ShopPackTranslation(); tr.languageId = t.languageId; tr.title = t.title; tr.description = t.description; return tr; });

    this.apiShopService.editPack(this.packId, this.name, startTimestamp, endTimestamp, this.shopItemId, translationsPayload, this.rewards, this.boosters).then(
      () => {
        this.isLoading = false;
        this.modal.hide();
        this.alertsService.displayAlertSuccess('Pack updated !');
        if (this.callbackSaved) { this.callbackSaved(); }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error updating pack : ' + error);
      }
    );
  }

  private timestampToDateString(timestamp: number): string {
    const d = new Date(timestamp * 1000);
    const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = d.getUTCDate().toString().padStart(2, '0');
    const hours = d.getUTCHours().toString().padStart(2, '0');
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    return `${d.getUTCFullYear()}-${month}-${day}T${hours}:${minutes}`;
  }
}
